import { html, css } from 'lit-element';
import { TmnConfirmationToolBase } from '@trumedianetworks/tmn-core-components/src/tmn-tool-base';
import { connect } from '../store/connect';
import { selectors } from '../site/';

class SoftDeactivateBtn extends TmnConfirmationToolBase {
    constructor() {
        super();
        this.disabled = false;
        this.actionLabel = 'Soft Deactivate';
        this.itemType = 'site';
        this.path = 'authorizations.softDeactivation';
    }

    static get styles() {
        return [
            ...super.styles,
            css`
                :host {
                    display: flex;
                    flex-flow: column;
                    position: relative;
                }
                tmn-select {
                    min-width: 200px;
                    max-width: 200px;
                }
                .select-container {
                    display: flex;
                    flex-flow: row;
                    align-items: flex-end;
                    margin-top: 10px;
                }
                .select-container tmn-icon-button {
                    margin-left: 6px;
                    margin-bottom: 2px;
                }
            `,
        ];
    }

    static get properties() {
        return {
            disabled: {
                type: Boolean,
            },
            eventData: {
                type: Object,
            },
            actionLabel: {
                type: String,
            },
            value: {
                type: String,
            },
            valueStaged: {
                type: String,
            },
            path: {
                type: String,
            },
            itemTemplate: {
                type: Object,
            },
        };
    }

    getButtonLabel() {
        return html`${this.actionLabel} selected`;
    }

    getToolTitle() {
        return html`${this.actionLabel} site`;
    }

    getConfirmationLabel() {
        return `Are you sure want to soft deactivate this site?`;
    }

    renderToolContent() {
        return html`
            <tmn-grid class="modal-content" container direction="row" justify="center" spacing="1">
                <div class="confirmation-label">${this.getConfirmationLabel()}</div>
                ${this.renderSelect()} ${this.renderErrors()}
            </tmn-grid>
        `;
    }

    renderSelect() {
        const { itemTemplate, value, valueStaged } = this;
        const { type, options } = itemTemplate;
        const multiple = type === 'MULTISELECT';

        if (!options?.length) {
            return html`<div>no options</div>`;
        }
        const currentValue = valueStaged || value;
        const dataForSelect = options.map((o) => {
            if (typeof o === 'string' || typeof o === 'number') {
                // turn into an object meeting the appropriate criteria
                return { name: o, displayKey: o.toString() };
            }
            if (!o.name) {
                o.name = o.displayKey;
            }
            if (!o.displayKey) {
                o.displayKey = o.name;
            }
            return o;
        });
        const valueForLookup = Array.isArray(currentValue) ? currentValue : [currentValue];
        const selections = dataForSelect.filter((d) => valueForLookup.includes(d.name));
        const displayLabel = 'Deactivation Message:';
        return html`<div class="select-container">
            <tmn-select
                .multiple=${multiple}
                @tmn-selections-changed=${this.handleSelectChanged}
                .data=${dataForSelect}
                .label=${displayLabel}
                .selections=${selections}></tmn-select>
        </div>`;
    }

    renderToolFooter() {
        const valueSelected = this.valueStaged;
        const disabled = !valueSelected;
        return html`
            <tmn-grid class="footer" container direction="row" justify="center" spacing="1">
                <tmn-grid item>
                    <tmn-button
                        class="confirm-button"
                        variant="outlined"
                        color="primary"
                        .disabled=${disabled}
                        @click="${this._handleConfirm}">
                        ${this.getConfirmButtonLabel()}
                    </tmn-button>
                </tmn-grid>
                <tmn-grid item>
                    <tmn-button
                        class="cancel-button"
                        variant="outlined"
                        @click="${this._toggleDialog}">
                        ${this.getCancelButtonLabel()}
                    </tmn-button>
                </tmn-grid>
            </tmn-grid>
        `;
    }

    renderButton() {
        return html`<tmn-button
            .disabled=${this.disabled}
            @click=${this._toggleDialog}
            .eventData=${this.eventData}
            variant="outlined">
            Soft Deactivate
        </tmn-button>`;
    }

    handleSelectChanged(e) {
        const { itemTemplate } = this;
        const { type } = itemTemplate;
        const { detail } = e;
        const { selections } = detail;
        const values = selections.map((s) => s.name);
        const multiple = type === 'MULTISELECT';
        const value = multiple ? values : values[0];
        this.valueStaged = value;
    }

    _handleConfirm(e) {
        const { valueStaged } = this;
        // could pass in actions as an alternative
        const { eventData = {} } = this;
        this._toggleDialog();
        const event = new CustomEvent('soft-deactivate-confirmed', {
            detail: {
                ...eventData,
                softDeactivation: valueStaged,
            },
        });
        this.dispatchEvent(event);
        this.valueStaged = null;
    }
}

const mapStateToProps = (state, ownProps) => {
    const itemTemplateSelector = selectors.itemTemplateForPathFactory(ownProps.path);
    const valueSelector = selectors.siteElementValueByPathFactory(ownProps.path);
    return (state) => {
        return {
            value: valueSelector(state),
            itemTemplate: itemTemplateSelector(state),
        };
    };
};

window.customElements.define('soft-deactivate-btn', connect(mapStateToProps)(SoftDeactivateBtn));
